import type { DataPort } from '../ports/data-port.ts';
import type { Job } from '../domain/entities/job.ts';
import { itemEquipmentIds, type ScheduleItem } from '../domain/entities/schedule.ts';
import type { Todo } from '../domain/entities/todo.ts';
import type { DailyLog } from '../domain/entities/daily-log.ts';
import type { Equipment } from '../domain/entities/equipment.ts';
import {
  allClashes,
  bookingsInRange,
  type EquipmentBooking,
} from '../domain/entities/equipment-booking.ts';
import { equipmentLabel } from '../domain/entities/equipment.ts';
import { assistantTool } from './tools.ts';
import { topicsFor } from './product-knowledge.ts';
import { proposeShift } from './proposals.ts';
import type { WorkCalendar } from '../domain/work-calendar.ts';

/**
 * Running one tool the model asked for (SCRUM-223, Phase A).
 *
 * **In core, taking a `DataPort`**, so it is the person's own session that
 * reads — the browser passes its ports straight in. That is what makes
 * the security story simple: Firestore refuses the assistant on exactly
 * the documents it refuses them, with no second set of rules to keep in
 * step. It is also why this is unit-testable without a browser.
 *
 * Every result is **data, never instructions**. The caller hands what
 * comes back to the model as a tool result, and the system prompt says to
 * treat it as data — which matters here more than in most products,
 * because subcontractors and homeowners write into the records these
 * functions read.
 *
 * Results are deliberately **small and named**. A model handed forty
 * fields per row spends the context on ids nobody asked about, and a row
 * that says `jobId: 01J7Z…` with no name produces an answer quoting an
 * id. Each shape here is what somebody would say out loud.
 */
export interface ToolContext {
  readonly data: DataPort;
  readonly companyId: string;
  /** Today where the company is, for "next Monday" to mean anything. */
  readonly today: string;
  /**
   * A job's working calendar, which only the caller can assemble.
   *
   * A proposal's dates have to be counted over the job's own work week
   * and holidays, exactly as the write counts them — otherwise the
   * preview is wrong across every weekend.
   */
  readonly loadCalendar: (jobId: string) => Promise<WorkCalendar>;
}

export class ToolRefused extends Error {
  readonly tool: string;
  constructor(tool: string, message: string) {
    super(message);
    this.name = 'ToolRefused';
    this.tool = tool;
  }
}

type ToolInput = Record<string, unknown>;

const str = (input: ToolInput, key: string): string | undefined =>
  typeof input[key] === 'string' ? (input[key] as string) : undefined;

const range = (input: ToolInput): { from: string; to: string } | undefined => {
  const value = input.range as { from?: unknown; to?: unknown } | undefined;
  if (!value || typeof value.from !== 'string' || typeof value.to !== 'string') return undefined;
  return { from: value.from, to: value.to };
};

export async function runAssistantTool(
  context: ToolContext,
  name: string,
  rawInput: unknown,
): Promise<unknown> {
  const tool = assistantTool(name);
  // A model asking for a tool that does not exist is a model that has been
  // told about one that does not exist, or has invented it. Either way the
  // honest answer is a refusal it can report, not an empty result it would
  // read as "there is nothing".
  if (!tool) throw new ToolRefused(name, `There is no tool called ${name}.`);
  /**
   * A tool that changes something returns a **proposal**, and the caller
   * takes it to a person.
   *
   * `runAssistantTool` never writes. That is not a convention — it is why
   * no sentence in the company's data can move a date: subcontractors and
   * homeowners write the comments and logs these tools read, and the
   * assistant reads them while answering somebody else's question.
   */

  const input = (rawInput ?? {}) as ToolInput;
  const scope = { companyId: context.companyId };

  switch (name) {
    case 'list_jobs': {
      const page = await context.data.collection<Job>(scope, 'jobs').list();
      const wanted = str(input, 'search')?.toLowerCase();
      const status = str(input, 'status');
      return page.items
        .filter((job) => (status ? job.status === status : true))
        .filter((job) => (wanted ? job.name.toLowerCase().includes(wanted) : true))
        .map((job) => ({
          jobId: job.id,
          name: job.name,
          status: job.status,
          address: `${job.address.line1}, ${job.address.city} ${job.address.state}`,
          startDate: job.startDate,
          projectedEndDate: job.projectedEndDate,
        }));
    }

    case 'get_schedule': {
      const jobId = str(input, 'jobId');
      if (!jobId) throw new ToolRefused(name, 'That needs a job id from list_jobs.');
      const window = range(input);
      const page = await context.data
        .collection<ScheduleItem>({ companyId: context.companyId, jobId }, 'scheduleItems')
        .list();
      return page.items
        .filter((item) =>
          window ? item.startDate <= window.to && window.from <= item.endDate : true,
        )
        .sort((a, b) => a.startDate.localeCompare(b.startDate))
        .map((item) => ({
          title: item.title,
          startDate: item.startDate,
          endDate: item.endDate,
          workDays: item.workDays,
          progress: item.progress,
          completed: item.completed,
          equipmentIds: itemEquipmentIds(item),
        }));
    }

    case 'list_equipment': {
      const page = await context.data.collection<Equipment>(scope, 'equipment').list();
      const wanted = str(input, 'search')?.toLowerCase();
      return page.items
        .filter((item) => (wanted ? equipmentLabel(item).toLowerCase().includes(wanted) : true))
        .map((item) => ({
          equipmentId: item.id,
          name: equipmentLabel(item),
          status: item.status,
          nextMaintenanceOn: item.nextMaintenanceOn,
        }));
    }

    case 'equipment_in_use': {
      const window = range(input);
      if (!window) throw new ToolRefused(name, 'That needs a date range, as from and to.');
      const [bookings, fleet] = await Promise.all([
        context.data.collection<EquipmentBooking>(scope, 'equipmentBookings').list(),
        context.data.collection<Equipment>(scope, 'equipment').list(),
      ]);
      const only = str(input, 'equipmentId');
      const nameOf = (equipmentId: string) => {
        const machine = fleet.items.find((item) => item.id === equipmentId);
        return machine ? equipmentLabel(machine) : equipmentId;
      };

      const inRange = bookingsInRange(bookings.items, {
        startDate: window.from,
        endDate: window.to,
      }).filter((booking) => (only ? booking.equipmentId === only : true));

      return {
        inUse: inRange.map((booking) => ({
          equipment: nameOf(booking.equipmentId),
          job: booking.jobName,
          activity: booking.title,
          from: booking.startDate,
          to: booking.endDate,
          // Said, because a draft claim and a committed one are different
          // news to somebody deciding whether a machine is free.
          schedulePublished: booking.scheduleOnline,
        })),
        // Reported alongside rather than only on request: somebody asking
        // what is in use next Monday wants to know if two jobs want the
        // same machine that day.
        doubleBooked: allClashes(inRange).map((clash) => ({
          equipment: nameOf(clash.equipmentId),
          between: [clash.booking.jobName, clash.against.jobName],
          from: clash.from,
          to: clash.to,
        })),
        // So the model does not report "nothing is in use" when the real
        // answer is "nothing has been booked yet".
        fleetSize: fleet.items.length,
      };
    }

    case 'list_todos': {
      const jobId = str(input, 'jobId');
      if (!jobId) throw new ToolRefused(name, 'That needs a job id from list_jobs.');
      const page = await context.data
        .collection<Todo>({ companyId: context.companyId, jobId }, 'todos')
        .list();
      const openOnly = input.openOnly === true;
      return page.items
        .filter((todo) => (openOnly ? !todo.completed : true))
        .map((todo) => ({
          title: todo.title,
          // A to-do is due on a date **or** when a schedule item finishes.
          // Flattened to a sentence rather than passed through as a tagged
          // union: the model relays what it is given, and "kind:
          // scheduleItem" is not an answer anybody wants read back.
          due:
            todo.due?.kind === 'date'
              ? todo.due.date
              : todo.due?.kind === 'scheduleItem'
                ? 'when a schedule activity finishes'
                : undefined,
          priority: todo.priority,
          completed: todo.completed,
        }));
    }

    case 'list_daily_logs': {
      const jobId = str(input, 'jobId');
      if (!jobId) throw new ToolRefused(name, 'That needs a job id from list_jobs.');
      const window = range(input);
      const page = await context.data
        .collection<DailyLog>({ companyId: context.companyId, jobId }, 'dailyLogs')
        .list();
      return page.items
        .filter((log) => (window ? log.date >= window.from && log.date <= window.to : true))
        .sort((a, b) => b.date.localeCompare(a.date))
        .map((log) => ({ date: log.date, notes: log.notes, weather: log.weather }));
    }

    case 'propose_shift_work': {
      const jobId = str(input, 'jobId');
      if (!jobId) throw new ToolRefused(name, 'That needs a job id from list_jobs.');
      const days = typeof input.days === 'number' ? Math.trunc(input.days) : Number.NaN;
      if (!Number.isInteger(days) || days === 0) {
        throw new ToolRefused(name, 'That needs a whole number of work days, and not zero.');
      }
      const scope = str(input, 'scope');
      if (scope !== 'remaining' && scope !== 'all' && scope !== 'phase') {
        throw new ToolRefused(
          name,
          'Say whether this is the remaining work, all of it, or a phase.',
        );
      }
      const phaseId = str(input, 'phaseId');
      if (scope === 'phase' && !phaseId) {
        // Refused rather than widened. A phase nobody named used to mean
        // every ungrouped item, which would propose moving the whole job.
        throw new ToolRefused(name, 'Which phase? Read the schedule first and name it.');
      }

      const [page, calendar] = await Promise.all([
        context.data
          .collection<ScheduleItem>({ companyId: context.companyId, jobId }, 'scheduleItems')
          .list(),
        context.loadCalendar(jobId),
      ]);

      const proposal = proposeShift(calendar, page.items, {
        jobId,
        byWorkingDays: days,
        scope,
        ...(phaseId ? { phaseId } : {}),
        today: context.today,
      });

      return {
        // Named so the browser can tell a proposal from an answer and put
        // it behind a confirmation rather than printing it.
        proposal,
        note: 'Nothing has changed. Tell them what this would do and that they need to confirm it.',
      };
    }

    case 'explain_feature': {
      const topic = str(input, 'topic');
      if (!topic) throw new ToolRefused(name, 'That needs a topic to explain.');
      const found = topicsFor(topic);
      if (found.length === 0) {
        // A refusal the model can relay, rather than an empty result it
        // would turn into a confident answer about a feature that does
        // not exist.
        throw new ToolRefused(
          name,
          `There is nothing written about "${topic}". Say so rather than describing it from general knowledge — this product does not necessarily work the way others do.`,
        );
      }
      return {
        topics: found.map((entry) => ({ about: entry.key, says: entry.summary })),
        // The one instruction in a tool result, and it is about how to
        // use the result rather than about what to do next.
        note: 'This is what Buildmate actually does. Prefer it over anything you know about construction software generally.',
      };
    }

    default:
      // A tool in the catalog with no implementation here. Refused loudly
      // rather than returning nothing, because "no result" is a sentence
      // the model would relay as fact.
      throw new ToolRefused(name, `${name} is not wired up yet.`);
  }
}
